import { environment } from '@environment/environment';
import { IIntegration } from '@shared/interfaces/integration.interface';
import { ISetting } from '@shared/interfaces/setting.interface';

const assetsUrl = `${environment.ENDPOINTS.ASSETS}/images/integrations`;

/**
 * UI Metadata for Integrations
 *
 * This list provides UI-specific metadata (logos, display preferences) for integrations.
 * The actual list of AVAILABLE integrations is now fetched from the backend API.
 *
 * NEW BEHAVIOR (Dynamic Discovery):
 * - Backend API (/api/sponge/integrations) provides: id, ats_name, description, auth_strategy
 * - This helper provides: logo URLs and display preferences
 * - IntegrationsAuthorizationsService merges both sources
 *
 * To add a new ATS:
 * 1. Backend: Create config/integrations/newats.yml
 * 2. Frontend (optional): Add entry here for logo URL
 *    - If not added here, uses a default placeholder logo
 *    - To use logo from config, add logo_url field to YAML
 *
 * Legacy integrations (non-Sponge) still require entries here.
 */
export const externalAuthorizations: IIntegration[] = [
  // {
  //   id: 'avionte',
  //   label: 'avionte',
  //   isAuthorized: false,
  //   isLoading: false,
  //   isSetup: false,
  //   isAvailable: false,
  //   logo: `${assetsUrl}/avionte.png`
  // },
  {
    id: 'bullhorn',
    label: 'bullhorn',
    isAuthorized: false,
    isLoading: false,
    isSetup: false,
    isAvailable: true,
    logo: `${assetsUrl}/bullhorn.png`
  },
  {
    id: 'erecruit',
    label: 'erecruit',
    isAuthorized: false,
    isLoading: false,
    isSetup: false,
    isAvailable: true,
    logo: `${assetsUrl}/erecruit.png`
  },
  // {
  //   id: 'icims',
  //   label: 'icims',
  //   isAuthorized: false,
  //   isLoading: false,
  //   isSetup: false,
  //   isAvailable: false,
  //   logo: `${assetsUrl}/icims.png`
  // },
  {
    id: 'jobadder',
    label: 'jobadder',
    isAuthorized: false,
    isLoading: false,
    isSetup: false,
    isAvailable: true,
    logo: `${assetsUrl}/jobadder.png`
  },
  {
    id: 'jobdiva',
    label: 'jobdiva',
    isAuthorized: false,
    isLoading: false,
    isSetup: false,
    isAvailable: true,
    logo: `${assetsUrl}/jobdiva.png`
  },
  // {
  //   id: 'salesforce',
  //   label: 'salesforce',
  //   isAuthorized: false,
  //   isLoading: false,
  //   isSetup: false,
  //   isAvailable: true,
  //   logo: `${assetsUrl}/salesforce.png`
  // },
  // {
  //   id: 'salesforce_isv',
  //   label: `salesforce <span class='text-uppercase'>ISV</span>`,
  //   isAuthorized: false,
  //   isLoading: false,
  //   isSetup: false,
  //   isAvailable: true,
  //   logo: `${assetsUrl}/salesforce.png`
  // },
  // {
  //   id: 'taleo',
  //   label: 'taleo',
  //   isAuthorized: false,
  //   isLoading: false,
  //   isSetup: false,
  //   isAvailable: false,
  //   logo: `${assetsUrl}/taleo.png`
  // },
  {
    id: 'target_recruit',
    label: 'target recruit',
    isAuthorized: false,
    isLoading: false,
    isSetup: false,
    isAvailable: true,
    logo: `${assetsUrl}/target-recruit.png`
  },

  {
    id: 'greenhouse',
    label: 'greenhouse',
    isAuthorized: false,
    isLoading: false,
    isSetup: false,
    isAvailable: true,
    logo: `http://127.0.0.1:5000/assets/greenhouse.png`
  }
];

/**
 * Sponge integrations the backend keeps `listed: false` (pre-GA) but that a
 * firm can already use once its opt-in setting is on. The Integrations tab
 * adds the card client-side for those firms only; the backend gates every
 * endpoint (config form, authorize, status, diagnostics) on the same setting,
 * so a card shown to an un-flagged firm would be a dead end.
 *
 * Once the backend flips `listed: true` the entry keeps supplying the logo /
 * label metadata and the gate becomes a no-op (the card is already listed).
 *
 * Logos live in the shared `src/assets/images/integrations/` folder (copied
 * into every project's `/assets`) rather than the region asset buckets, so a
 * new integration ships without a per-region S3 upload.
 */
export interface IGatedSpongeIntegration {
  setting: keyof ISetting;
  integration: IIntegration;
}

export const gatedSpongeIntegrations: IGatedSpongeIntegration[] = [
  {
    setting: 'is_workday_sponge_enabled',
    integration: {
      id: 'workday',
      label: 'workday',
      isAuthorized: false,
      isLoading: false,
      isSetup: false,
      isAvailable: true,
      logo: '/assets/images/integrations/workday.svg'
    }
  }
];

/**
 * UI metadata (logo, label) for an integration id, whether it's a legacy
 * entry or a gated Sponge one. Undefined for backend-only integrations.
 */
export const findIntegrationMetadata = (
  id: string
): IIntegration | undefined =>
  externalAuthorizations.find((ea) => ea.id === id) ??
  gatedSpongeIntegrations.find((g) => g.integration.id === id)?.integration;
